import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatDateTime } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { PageHeader, ChannelBadge, StatusBadge, EmptyState, useConfirm } from "../../components/common";
import { MESSAGE_STATUS, MESSAGE_TYPES, timeAgo, waLink, channelLabel } from "../../lib/channels";
import { Search, Phone, MessageCircle, Mail, Inbox, Download, Trash2, Wrench, MapPin, Cpu, Globe2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_ORDER = ["all", "yeni", "okundu", "donus_yapildi", "isleme_alindi", "kapandi", "spam"];

export default function Messages() {
  const [ask, confirmEl] = useConfirm();
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [convOpen, setConvOpen] = useState(false);
  const [techs, setTechs] = useState([]);
  const [brands, setBrands] = useState([]);
  const [conv, setConv] = useState({ technician_id: "", brand_id: "", device_type: "kombi", priority: "normal", scheduled_at: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/messages", { params: { ...(type !== "all" ? { type } : {}), ...(q ? { q } : {}) } });
    setList(data);
    setSel((cur) => (cur ? data.find((m) => m.id === cur.id) || null : cur));
  }, [type, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    api.get("/technicians").then((r) => setTechs(r.data.filter((t) => t.is_active)));
    api.get("/brands").then((r) => setBrands(r.data));
  }, []);

  const counts = useMemo(() => {
    const c = { all: list.length };
    list.forEach((m) => { c[m.status] = (c[m.status] || 0) + 1; });
    return c;
  }, [list]);
  const shown = list.filter((m) => status === "all" || m.status === status);

  const open = async (m) => {
    setSel(m);
    setNote(m.note || "");
    if (m.status === "yeni") {
      const { data } = await api.put(`/messages/${m.id}`, { status: "okundu" });
      setList((l) => l.map((x) => (x.id === m.id ? data : x)));
      setSel(data);
    }
  };

  const update = async (patch, msg) => {
    const { data } = await api.put(`/messages/${sel.id}`, patch);
    setSel(data);
    setList((l) => l.map((x) => (x.id === data.id ? data : x)));
    if (msg) toast.success(msg);
  };

  const remove = async () => {
    if (!(await ask("Bu mesaj kalıcı olarak silinsin mi?"))) return;
    await api.delete(`/messages/${sel.id}`);
    setSel(null);
    load();
    toast.success("Silindi");
  };

  const convert = async () => {
    try {
      const body = { ...conv, technician_id: conv.technician_id === "none" ? "" : conv.technician_id, brand_id: conv.brand_id === "none" ? "" : conv.brand_id };
      const { data } = await api.post(`/messages/${sel.id}/convert`, body);
      toast.success(`Servis kaydı oluşturuldu: ${data.job_number}`);
      setConvOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const download = async () => {
    const res = await api.get("/export/messages", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data); a.download = "iletisim-mesajlari.csv"; a.click();
  };

  const src = sel?.source || {};

  return (
    <div className="space-y-6" data-testid="messages-page">
      <PageHeader eyebrow="Web Formları" title="İletişim Mesajları" subtitle="Sitedeki iletişim ve servis talep formundan gelen mesajlar. Müşteri hangi kanaldan geldiyse burada görünür.">
        <Button variant="outline" onClick={download} data-testid="export-messages"><Download className="h-4 w-4 mr-2" />CSV</Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2" data-testid="message-status-chips">
        {STATUS_ORDER.map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`chip-${s}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {s === "all" ? "Tümü" : MESSAGE_STATUS[s].label} <span className="opacity-60 ml-1">{counts[s] || 0}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Liste */}
        <Card className="lg:col-span-2 border border-slate-200 shadow-none overflow-hidden flex flex-col max-h-[calc(100vh-18rem)] min-h-[420px]">
          <div className="p-3 border-b border-slate-200 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, telefon, mesaj..." className="pl-9" data-testid="message-search" />
            </div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm türler</SelectItem>
                {Object.entries(MESSAGE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-y-auto flex-1">
            {shown.length === 0 && <EmptyState icon={Inbox} title="Mesaj yok" hint="Sitedeki formdan gelen mesajlar burada listelenir." />}
            {shown.map((m) => (
              <button key={m.id} onClick={() => open(m)} data-testid={`message-item-${m.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${sel?.id === m.id ? "bg-blue-50/60" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`h-2 w-2 rounded-full shrink-0 ${MESSAGE_STATUS[m.status]?.dot}`} />
                    <span className={`truncate ${m.status === "yeni" ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>{m.name}</span>
                  </div>
                  <span className="text-[11px] text-slate-400 shrink-0">{timeAgo(m.created_at)}</span>
                </div>
                <div className="text-sm text-slate-500 truncate mt-0.5">{m.message || MESSAGE_TYPES[m.type]}</div>
                <div className="flex items-center gap-2 mt-1.5">
                  <ChannelBadge channel={m.channel} />
                  {m.city && <span className="text-[11px] text-slate-400">{m.city}</span>}
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Detay */}
        <Card className="lg:col-span-3 border border-slate-200 shadow-none p-6" data-testid="message-detail">
          {!sel ? (
            <EmptyState icon={Inbox} title="Bir mesaj seçin" hint="Soldaki listeden bir mesaja tıklayın." />
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="font-display text-2xl font-bold text-slate-900">{sel.name}</h2>
                  <div className="text-sm text-slate-500 mt-0.5">{MESSAGE_TYPES[sel.type] || sel.type} • {formatDateTime(sel.created_at)}</div>
                </div>
                <StatusBadge map={MESSAGE_STATUS} value={sel.status} />
              </div>

              <div className="flex flex-wrap gap-2">
                <a href={`tel:${sel.phone}`}><Button variant="outline" size="sm" data-testid="msg-call"><Phone className="h-4 w-4 mr-2" />{sel.phone}</Button></a>
                <a href={waLink(sel.phone, `Merhaba ${sel.name}, ServisPlus'a ilettiğiniz talep hakkında yazıyorum.`)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200"><MessageCircle className="h-4 w-4 mr-2" />WhatsApp</Button></a>
                {sel.email && <a href={`mailto:${sel.email}`}><Button variant="outline" size="sm"><Mail className="h-4 w-4 mr-2" />{sel.email}</Button></a>}
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-slate-50"><div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />Konum</div><div className="font-medium mt-0.5">{[sel.district, sel.city].filter(Boolean).join(" / ") || "-"}</div></div>
                <div className="p-3 rounded-lg bg-slate-50"><div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Cpu className="h-3 w-3" />Cihaz</div><div className="font-medium mt-0.5">{[sel.device_type, sel.brand].filter(Boolean).join(" • ") || "-"}</div></div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Mesaj</div>
                <div className="p-4 rounded-lg border border-slate-200 whitespace-pre-wrap text-slate-800 min-h-[72px]">{sel.message || <span className="text-slate-400">Mesaj yazılmamış</span>}</div>
              </div>

              <div className="p-4 rounded-lg bg-blue-50/50 border border-blue-100">
                <div className="text-[11px] uppercase tracking-wider font-bold text-blue-800 mb-2 flex items-center gap-1"><Globe2 className="h-3 w-3" />Müşteri nereden geldi?</div>
                <div className="flex items-center gap-2 flex-wrap mb-2"><ChannelBadge channel={sel.channel} /><span className="text-sm text-slate-600">{channelLabel(sel.channel)}{src.source_name ? ` • ${src.source_name}` : ""}</span></div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                  <div><dt className="inline text-slate-400">Giriş sayfası: </dt><dd className="inline">{src.landing_page || "-"}</dd></div>
                  <div><dt className="inline text-slate-400">Formu doldurduğu sayfa: </dt><dd className="inline">{sel.page || "-"}</dd></div>
                  <div><dt className="inline text-slate-400">Kampanya: </dt><dd className="inline">{src.campaign || "-"}</dd></div>
                  <div><dt className="inline text-slate-400">Anahtar kelime: </dt><dd className="inline">{src.term || "-"}</dd></div>
                  <div><dt className="inline text-slate-400">Cihaz: </dt><dd className="inline">{src.device || "-"}</dd></div>
                  <div><dt className="inline text-slate-400">IP: </dt><dd className="inline font-mono">{sel.ip || "-"}</dd></div>
                </dl>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Durum</Label>
                  <Select value={sel.status} onValueChange={(v) => update({ status: v }, "Durum güncellendi")}>
                    <SelectTrigger data-testid="msg-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(MESSAGE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  {sel.job_id ? (
                    <div className="w-full text-sm p-2.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 flex items-center gap-2"><ExternalLink className="h-4 w-4" />Servis kaydı: <b className="font-mono">{sel.job_number}</b></div>
                  ) : (
                    <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => setConvOpen(true)} data-testid="msg-convert"><Wrench className="h-4 w-4 mr-2" />Servis Kaydına Dönüştür</Button>
                  )}
                </div>
              </div>

              <div>
                <Label>İç not (müşteri görmez)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Arandı, yarın 14:00'e randevu verildi..." data-testid="msg-note" />
                <div className="flex justify-between mt-2">
                  <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={remove} data-testid="msg-delete"><Trash2 className="h-4 w-4 mr-1.5" />Sil</Button>
                  <Button size="sm" variant="outline" onClick={() => update({ note }, "Not kaydedildi")} data-testid="msg-save-note">Notu Kaydet</Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Dialog open={convOpen} onOpenChange={setConvOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Servis Kaydına Dönüştür</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Müşteri kartı otomatik açılır (telefon numarası kayıtlıysa mevcut müşteri kullanılır) ve kanal bilgisi <b>{channelLabel(sel?.channel)}</b> olarak servis kaydına işlenir.</p>
          <div className="space-y-3">
            <div>
              <Label>Teknisyen ata (opsiyonel)</Label>
              <Select value={conv.technician_id || "none"} onValueChange={(v) => setConv({ ...conv, technician_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Sonra atanacak</SelectItem>{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Marka</Label>
                <Select value={conv.brand_id || "none"} onValueChange={(v) => setConv({ ...conv, brand_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Belirtilmedi</SelectItem>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cihaz tipi</Label>
                <Select value={conv.device_type} onValueChange={(v) => setConv({ ...conv, device_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="kombi">Kombi</SelectItem><SelectItem value="klima">Klima</SelectItem><SelectItem value="diger">Diğer</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Öncelik</Label>
                <Select value={conv.priority} onValueChange={(v) => setConv({ ...conv, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="dusuk">Düşük</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="yuksek">Yüksek</SelectItem><SelectItem value="acil">Acil</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Randevu</Label><Input type="datetime-local" value={conv.scheduled_at} onChange={(e) => setConv({ ...conv, scheduled_at: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvOpen(false)}>Vazgeç</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={convert} data-testid="msg-convert-confirm">Servis Kaydı Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmEl}
    </div>
  );
}
